import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { getAuthTokenFromCookies } from "../../common/utils";
import SvgLoadingAnimation from "../loading";

interface RequireAuthProps {
  children?: React.ReactNode;
}

/**
 * 認証ガード。未サインイン（トークン無し / 401 / ユーザー情報取得失敗）の場合は
 * 現在のパス（クエリ込み）を `redirect` に保持してサインインページへ誘導する。
 * GlobalWrapper の外側に置くことで、未認証時は GlobalHeader のエラー表示に
 * 到達せず即リダイレクトできる。
 */
const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const location = useLocation();
  const token = getAuthTokenFromCookies();
  const { data: currentUser, isLoading, error } = useGetCurrentUserQuery(undefined, {
    // トークンが無ければ /api/me を叩かずに即リダイレクトする
    skip: !token,
  });

  const redirectToSignin = () => {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?redirect=${redirect}`} replace />;
  };

  if (!token) {
    return redirectToSignin();
  }

  // 認証チェックが確定するまで待機し、認証済みページのちらつきを防ぐ
  if (isLoading) {
    return <SvgLoadingAnimation />;
  }

  if (error || !currentUser?.data) {
    return redirectToSignin();
  }

  return children ? <>{children}</> : <Outlet />;
};

export default RequireAuth;
